import { useState } from 'react';
import { CoachCard } from '../concept/coach-card';
import { ActionButton } from '../concept/action-button';
import { useToast } from '../primitives/use-toast';
import { EmailLinkAction } from './email-link-action';
import type { SendLinkKind, SendLinkResult } from '../../lib/dashboard/sendLink';

/**
 * One home for the two links a coach hands to a client.
 *
 * Replaces a "Share" card whose two buttons — "Client view" and "Export PDF" —
 * were wired to nothing at all: props the dashboard never passed, so they
 * looked like features and did nothing when pressed.
 *
 * The two links are not the same thing and are labelled as what they let the
 * client do, not as what they technically are:
 *
 *   Scan link       opens the capture flow on the client's own phone. One
 *                   session, carrying its own code, so the client needs no
 *                   account. Pressing again issues a fresh one.
 *   Dashboard link  the standing share of their results. Owned by ShareDialog,
 *                   which can also revoke it, so it is passed in rather than
 *                   duplicated here.
 *
 * With `onEmailLink`, each link can also be emailed to the client's stored
 * address (SF-91) — the dashboard link only while sharing is on.
 */
export const SendToClientCard: React.FC<{
  clientName: string;
  /** Creates a session and returns its absolute scan URL. */
  onCreateScanLink: () => Promise<string>;
  dashboardLink?: string;
  /** ShareDialog's trigger, so revoking stays in one place. */
  dashboardAction?: React.ReactNode;
  /** Where an emailed link goes; shown under the action. */
  clientEmail?: string;
  /** Emails a server-built link. Absent, no email action is offered. */
  onEmailLink?: (kind: SendLinkKind) => Promise<SendLinkResult>;
  lastSent?: Partial<Record<SendLinkKind, string>>;
  /** What the person is called here: "client", or "customer" on retail. */
  noun?: string;
  /** What the dashboard link shows them; retail has no Goal column. */
  dashboardSummary?: string;
  className?: string;
}> = ({
  clientName,
  onCreateScanLink,
  dashboardLink,
  dashboardAction,
  clientEmail,
  onEmailLink,
  lastSent,
  noun = 'client',
  dashboardSummary = 'Then, Now and Goal',
  className,
}) => {
  const { toast } = useToast();
  const [scanLink, setScanLink] = useState<string>();
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      setScanLink(await onCreateScanLink());
    } catch (e) {
      toast({
        title: 'Could not create a scan link',
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${what} copied`, description: `Send it to ${clientName}.` });
    } catch {
      // Refused without a gesture, and on http origins. The field is on screen
      // and selectable either way, so this is a nudge rather than a failure.
      toast({ title: 'Copy it from the field', variant: 'destructive' });
    }
  };

  const createButton = (
    <ActionButton
      className="max-sm:min-h-11"
      disabled={busy}
      onClick={create}
      testId="create-scan-link"
    >
      {busy ? 'Creating…' : scanLink ? 'New link' : 'Create scan link'}
    </ActionButton>
  );

  return (
    <CoachCard title={`Send to ${noun}`} testId="send-to-client" className={className}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="text-[12.5px] font-medium leading-snug">Scan link</div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            {clientName} opens this on their own phone and scans themselves. No
            account needed.
          </p>
          {scanLink ? (
            <LinkRow
              value={scanLink}
              testId="scan-link"
              onCopy={() => copy(scanLink, 'Scan link')}
            />
          ) : null}
          {onEmailLink ? (
            <EmailLinkAction
              kind="scan"
              clientName={clientName}
              clientEmail={clientEmail}
              onSend={onEmailLink}
              lastSentAt={lastSent?.scan}
              before={createButton}
            />
          ) : (
            <div className="mt-0.5 self-start">{createButton}</div>
          )}
        </div>

        <div className="flex flex-col gap-1.5 border-t border-border pt-3.5">
          <div className="text-[12.5px] font-medium leading-snug">Dashboard link</div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Their results — {dashboardSummary} — without your notes.
          </p>
          {dashboardLink ? (
            <LinkRow
              value={dashboardLink}
              testId="dashboard-link"
              onCopy={() => copy(dashboardLink, 'Dashboard link')}
            />
          ) : null}
          {onEmailLink ? (
            <EmailLinkAction
              kind="dashboard"
              available={Boolean(dashboardLink)}
              clientName={clientName}
              clientEmail={clientEmail}
              onSend={onEmailLink}
              lastSentAt={lastSent?.dashboard}
              before={dashboardAction}
            />
          ) : (
            dashboardAction && <div className="mt-0.5 self-start">{dashboardAction}</div>
          )}
        </div>
      </div>
    </CoachCard>
  );
};

const LinkRow: React.FC<{ value: string; testId: string; onCopy: () => void }> = ({
  value,
  testId,
  onCopy,
}) => (
  <div className="flex gap-2">
    <input
      readOnly
      value={value}
      data-testid={testId}
      onFocus={(e) => e.currentTarget.select()}
      className="min-w-0 flex-1 rounded-lg border border-border bg-shell px-2.5 py-2 font-mono text-[10.5px] outline-none"
    />
    <ActionButton onClick={onCopy} testId={`${testId}-copy`}>
      Copy
    </ActionButton>
  </div>
);
